import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';
import { CSVLink } from 'react-csv';
import VerbButton, { VERB_BUTTON_TYPE } from '../../../../../../shared/components/VerbButton';
import VerbText from '../../../../../../shared/components/VerbText';
import { CompanyFormMemberTable } from './CompanyFormMemberTable';
import SearchForm from '../../../../../../shared/containers/forms/SearchForm';
import { getCompanyMembers } from '../../../../../../shared/utils/FetchUtils/CompanyFetchUtils';
import { RuntimeContext } from '../../../../../../../common/RuntimeContext';

export default class InvitedMembersTab extends Component {
    constructor(props) {
        super(props);
        this.csvLink = React.createRef();

        this.state = {
            membersToEmail: [],
            membersIDs: [],
            areAllMembersSelected: false,
            invitesCancelledSuccess: '',
            csvData: [],
            loading: false
        };
    }

    static propTypes = {
        company: PropTypes.object,
        sendEmailReminders: PropTypes.func,
        updatingMembers: PropTypes.bool,
        invitedMembers: PropTypes.array,
        totalInvitedMembersCount: PropTypes.number,
        emailSentCount: PropTypes.number,
        handleCompanyMemberSearch: PropTypes.func,
        handleClearSearchBar: PropTypes.func,
        searchHasEmptyResults: PropTypes.bool,
        realDeleteProfile: PropTypes.func,
        getCompanyMembersByTabName: PropTypes.func
    };

    static contextType = RuntimeContext;

    _selectToEmail = (user) =>
        this.setState((prevstate) => {
            const { membersToEmail } = prevstate;
            const { membersIDs } = prevstate;

            const indexOfMemberIfInList = membersToEmail.indexOf(user.email);
            if (indexOfMemberIfInList > -1) {
                membersToEmail.splice(indexOfMemberIfInList, 1);
                membersIDs.splice(indexOfMemberIfInList, 1);
            } else {
                membersToEmail.push(user.email);
                membersIDs.push(user._id);
            }
            return {
                membersToEmail,
                membersIDs
            };
        });

    _sendEmailReminders = () => {
        const { membersToEmail } = this.state;
        const { sendEmailReminders } = this.props;
        return sendEmailReminders(membersToEmail).then(() =>
            this.setState({ membersToEmail: [], membersIDs: [], areAllMembersSelected: false })
        );
    };

    selectOrUnselectAllUnregisteredMember = () => {
        const { invitedMembers } = this.props;
        const { areAllMembersSelected } = this.state;

        return this.setState({
            areAllMembersSelected: !areAllMembersSelected,
            membersToEmail: !areAllMembersSelected ? invitedMembers.map((memberProfile) => memberProfile.email) : [],
            membersIDs: !areAllMembersSelected ? invitedMembers.map((memberProfile) => memberProfile._id) : []
        });
    };

    handleAction = (selected) => {
        const { membersIDs } = this.state;
        const { realDeleteProfile, getCompanyMembersByTabName } = this.props;

        if (selected === 'SEND_REMINDER') {
            this.setState({ reminderLoading: true });
            this._sendEmailReminders()
                .then(() => getCompanyMembersByTabName({ clearSearch: true }))
                .then(() => {
                    this.setState({ reminderLoading: false });
                });
        } else if (selected === 'CANCEL_INVITE') {
            const confirmation = window.confirm('Are you sure you want to cancel the invite?');
            if (confirmation) {
                this.setState({ cancelLoading: true });
                const cancelledMessHolder =
                    membersIDs.length > 1
                        ? `${membersIDs.length} invites have been cancelled.`
                        : `1 invite has been cancelled.`;
                realDeleteProfile({ invitedUsersToDelete: membersIDs, pathsContext: this.context.pathsWeb })
                    .then(() => {
                        this.setState({
                            membersToEmail: [],
                            membersIDs: [],
                            areAllMembersSelected: false,
                            invitesCancelledSuccess: cancelledMessHolder
                        });
                        return getCompanyMembersByTabName({ clearSearch: true });
                    })
                    .then(() => {
                        this.setState({ cancelLoading: false });
                    });
            }
        }
    };

    _formatDate = (date) =>
        new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    _generateCSVData = () => {
        const { company, totalInvitedMembersCount } = this.props;
        const LIMIT = 1000;

        this.setState({ loading: true });

        // split requests based on LIMIT
        const getMembersPromises = new Array(Math.ceil(totalInvitedMembersCount / LIMIT)).fill(0).map((_, index) =>
            getCompanyMembers({
                companyId: company._id,
                query: {
                    invitedOrder: 'desc',
                    page: index,
                    limit: LIMIT
                },
                pathsContext: this.context.pathsWeb
            })
        );

        // responses = array of arrays [[], []]
        return Promise.all(getMembersPromises).then((responses) => {
            const registeredMembers = responses.flat(); // flatten array to be combined array of profiles

            // array of arrays [[], []]
            const users = registeredMembers.map(({ email, createdOn: invitedOn, invitedDateTime: lastReminder }) => [
                email,
                this._formatDate(invitedOn),
                lastReminder !== invitedOn ? this._formatDate(lastReminder) : null
            ]);

            const csvData = [['Email', 'Invited Date', 'Last Reminder Sent'], ...users];
            this.setState({ csvData, loading: false }, () => {
                this.csvLink.current.link.click();
            });
        });
    };

    render() {
        const {
            invitedMembers,
            company,
            updatingMembers,
            emailSentCount,
            handleCompanyMemberSearch,
            handleClearSearchBar,
            searchHasEmptyResults = false
        } = this.props;
        const {
            membersToEmail,
            areAllMembersSelected,
            invitesCancelledSuccess,
            csvData,
            loading,
            reminderLoading,
            cancelLoading
        } = this.state;
        const hasMembers = invitedMembers && invitedMembers.length;
        const csvFilename = !company ? 'Invited_Members' : `${company.name} Invited_Members`;

        return (
            <div className={'invited-members-tab-container'}>
                <div className={'row'} style={{ margin: '0px' }}>
                    <div className={'col-12 col-lg-8 form-action-buttons'} style={{ float: 'left' }}>
                        <VerbButton
                            buttonType={VERB_BUTTON_TYPE.PRIMARY_DEFAULT}
                            disabled={loading}
                            onClick={this._generateCSVData}
                            textId={loading ? 'LOADING' : 'ACTION_EXPORT_CSV'}
                            defaultMessage={loading ? 'LOADING' : 'EXPORT CSV'}
                            className={'form-button'}
                        />
                        <CSVLink ref={this.csvLink} className={'d-none'} data={csvData} filename={csvFilename} />
                        <VerbButton
                            buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                            disabled={!membersToEmail.length}
                            onClick={() => this.handleAction('SEND_REMINDER')}
                            textId={reminderLoading ? 'LOADING' : 'SEND_REMINDER'}
                            defaultMessage={reminderLoading ? 'LOADING' : 'Send Reminder'}
                            className={'form-button'}
                        />
                        <VerbButton
                            buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                            disabled={!membersToEmail.length}
                            onClick={() => this.handleAction('CANCEL_INVITE')}
                            textId={cancelLoading ? 'LOADING' : 'CANCEL_INVITE'}
                            defaultMessage={cancelLoading ? 'LOADING' : 'Cancel Invite'}
                            className={'form-button'}
                        />
                        {!!hasMembers && (
                            <div className={'row col-12 me-auto'}>
                                <div className={'row success-message me-auto invites-cancelled-margin'}>
                                    {invitesCancelledSuccess}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={'col-12 col-lg-4 ml-auto mt-2 mt-lg-0 company-member-search'}>
                        <SearchForm
                            indexName={'Profile'}
                            showBottomBorder={false}
                            showFullBorder={true}
                            handleSearchResults={handleCompanyMemberSearch}
                            onSubmit={handleCompanyMemberSearch}
                            onClearSearch={handleClearSearchBar}
                            pathsContext={this.context.pathsWeb}
                        />
                    </div>
                </div>
                {hasMembers ? (
                    <Fragment>
                        <div className={'col-12'}>
                            <div className={cns('', { invisible: emailSentCount <= 0 })}>
                                <VerbText
                                    messageId={'COMPANY_REMINDERS_SENT'}
                                    defaultMessage={'Reminders have been sent to {MemberCount} members.'}
                                    valuesToInject={{ MemberCount: emailSentCount }}
                                />
                            </div>
                        </div>
                        <div className={'col-12'}>
                            <small className={'no-results'}>
                                {searchHasEmptyResults && (!invitedMembers || !invitedMembers.length)
                                    ? `Sorry!\nYour search didn't return any results.`
                                    : null}
                            </small>
                        </div>
                        <CompanyFormMemberTable
                            memberProfiles={invitedMembers}
                            areAllMembersSelected={areAllMembersSelected}
                            selectOrUnselectAllUnregisteredMember={this.selectOrUnselectAllUnregisteredMember}
                            company={company}
                            isUpdatingMembers={updatingMembers}
                            fontAwesomeIcon={'fa-bell'}
                            handleClick={this._selectToEmail}
                            selectedList={membersToEmail}
                            selectedField={'email'}
                            actionTitle={'Remind'}
                            dateKey={'createdOn'}
                            dateTitle={'Invited Date'}
                        />
                    </Fragment>
                ) : (
                    <div className={'col-sm-12'}>
                        <small>
                            <VerbText
                                messageId={'COMPANY_NO_INVITED_MEMBERS'}
                                defaultMessage={`All invited users have registered.`}
                            />
                        </small>
                    </div>
                )}
            </div>
        );
    }
}
